import { PrismicRichText } from "@prismicio/react";
import { useContext } from "react";
import { RichTextField } from "@prismicio/types";
import { PrismicNextImage } from "@prismicio/next";
import { mergeStyles } from "hyam-core/utils";
import { styles as moduleStyles } from "./styles";
import { twMerge } from "hyam-core/utils/tailwindMerge";
import _ from "lodash";
import { MotionOl, MotionUl, makeMotionComponent } from "hyam-core/components";
import { HyamPrismicLink } from "../HyamPrismicLink";
import {
  HeadingTagContext,
  RichTextHeadingType,
  RichTextHtmlTag,
} from "./HeadingTagContext";
import { superscriptSymbols } from "hyam-core/utils/superscriptSymbols";
import { Fragment } from "react";

export type HyamPrismicRichTextProps = {
  field: RichTextField;
  styles?: Partial<typeof moduleStyles>;
  stylesReplace?: Partial<typeof moduleStyles>;
  motionDisabled?: boolean;
  /**
   * Renders a different HTML tag for a heading type without changing its
   * styling, e.g. { heading1: "h3" }. Takes priority over the tags provided by
   * HeadingTagContext.
   */
  htmlTagReplace?: Partial<Record<RichTextHeadingType, RichTextHtmlTag>>;
};

type VariableTextProps = {
  children: JSX.Element[];
  styles: typeof moduleStyles;
  stylesReplace: typeof moduleStyles;
  type: PrismicRichTextTags;
  motionDisabled: boolean;
  htmlTagReplace?: HyamPrismicRichTextProps["htmlTagReplace"];
};

type PrismicRichTextTags =
  | "paragraph"
  | "heading1"
  | "heading2"
  | "heading3"
  | "heading4"
  | "heading5"
  | "heading6";

type HTMLTextTags = "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

const richTextTagToHtmlTag: Record<PrismicRichTextTags, HTMLTextTags> = {
  paragraph: "p",
  heading1: "h1",
  heading2: "h2",
  heading3: "h3",
  heading4: "h4",
  heading5: "h5",
  heading6: "h6",
};

const htmlTagToRichTextTag: Record<HTMLTextTags, PrismicRichTextTags> = _.invert(
  richTextTagToHtmlTag,
) as Record<HTMLTextTags, PrismicRichTextTags>;

/**
 * Maps a custom Prismic label (e.g. "tag-h3") to the rich text tag whose
 * style it should borrow. Returns undefined for labels that don't follow
 * the "tag-*" convention.
 */
const getRichTextTagFromLabel = (label: string) => {
  const htmlTag = label.match(/^tag-(.*)$/)?.[1] as HTMLTextTags | undefined;
  return htmlTag && htmlTagToRichTextTag[htmlTag];
};

/**
 * Renders text HTML tags with several flexible options for styling and HTML tag
 * replacement. These flexible options allows us to decouple html tags from
 * styling.
 *
 * For example, using VariableText we can render
 * - h2 html text with h1 styling,
 * - p html text with h5 styling,
 * - etc.
 *
 * This functionality is needed for SEO and accessibility topics.
 */
const VariableText = (props: VariableTextProps) => {
  const { children, styles, stylesReplace, type, motionDisabled, htmlTagReplace } = props;
  const contextTags = useContext(HeadingTagContext);
  const tag =
    (type !== "paragraph" && (htmlTagReplace?.[type] || contextTags[type])) ||
    richTextTagToHtmlTag[type];

  const Tag = makeMotionComponent(tag);

  const style = stylesReplace.all ? stylesReplace.all : twMerge(styles[type], styles.all);

  return (
    <Tag motionDisabled={motionDisabled} className={style}>
      {children}
    </Tag>
  );
};

export const HyamPrismicRichText = ({
  field,
  styles: propsStyles = {},
  stylesReplace: propsStylesReplace = {},
  motionDisabled = true,
  htmlTagReplace,
}: HyamPrismicRichTextProps) => {
  const styles = mergeStyles(
    moduleStyles,
    propsStyles,
    propsStylesReplace,
    "HyamPrismicRichText",
  );
  const stylesReplace = propsStylesReplace as typeof moduleStyles;

  const props = { motionDisabled, styles, stylesReplace, htmlTagReplace };

  return (
    <PrismicRichText
      field={field}
      components={{
        paragraph: (element) => <VariableText {...element} {...props} />,
        heading1: (element) => <VariableText {...element} {...props} />,
        heading2: (element) => <VariableText {...element} {...props} />,
        heading3: (element) => <VariableText {...element} {...props} />,
        heading4: (element) => <VariableText {...element} {...props} />,
        heading5: (element) => <VariableText {...element} {...props} />,
        heading6: (element) => <VariableText {...element} {...props} />,
        // Every run of text in the rich text (headings, paragraphs, lists,
        // links) goes through here. Same line break handling as Prismic's
        // default span serializer, plus superscript ©, ® and ™.
        span: ({ text, key }) => {
          const lines = text.split("\n");

          return (
            <Fragment key={key}>
              {lines.map((line, i) => (
                <Fragment key={i}>
                  {i > 0 && <br />}
                  {superscriptSymbols(line)}
                </Fragment>
              ))}
            </Fragment>
          );
        },
        image: ({ node }) => (
          <PrismicNextImage className={twMerge(styles.image, styles.all)} field={node} />
        ),
        // Custom labels (e.g. "tag-h3") mark an inline run of text within a
        // block, most often a heading-styled line inside a field authored
        // with line breaks rather than separate paragraphs. Render just that
        // run with the matching heading style instead of retagging the
        // whole parent block.
        label: ({ node, children, key }) => {
          const richTextTag = getRichTextTagFromLabel(node.data.label);

          if (!richTextTag) {
            return (
              <span key={key} className={node.data.label}>
                {children}
              </span>
            );
          }

          return (
            <span key={key} className={twMerge(styles.all, styles[richTextTag], "block")}>
              {children}
            </span>
          );
        },
        hyperlink: ({ node, children, key }) => {
          return (
            <HyamPrismicLink
              className={twMerge(styles.hyperlink, styles.all)}
              key={key}
              field={node.data}
            >
              {children}
            </HyamPrismicLink>
          );
        },
        oList: ({ children }) => (
          <MotionOl
            motionDisabled={motionDisabled}
            className={twMerge(styles.oList, styles.all)}
          >
            {children}
          </MotionOl>
        ),
        oListItem: ({ children }) => (
          <li className={twMerge(styles.oListItem, styles.all)}>{children}</li>
        ),
        list: ({ children }) => (
          <MotionUl
            motionDisabled={motionDisabled}
            className={twMerge(styles.list, styles.all)}
          >
            {children}
          </MotionUl>
        ),
        listItem: ({ children }) => (
          <li className={twMerge(styles.listItem, styles.all)}>{children}</li>
        ),
      }}
    />
  );
};
